import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Building2, Loader2, AlertTriangle, BarChart3, RefreshCw, Truck, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import InitialStockDialog from "../components/InitialStockDialog";
import MomaReassortWidget from "../components/MomaReassortWidget";
import { NayaxMachineWidget } from "../components/NayaxMachineWidget";
import NayaxResyncDialog from "../components/NayaxResyncDialog";
import EmptyMachineDialog from "../components/distributors/EmptyMachineDialog";
import ContractShortcut from "../components/distributors/ContractShortcut";
import { isStockDepot } from "../lib/stockDepot";

function daysToDlc(dlc) {
  if (!dlc) return null;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dlc + "T00:00:00");
    return Math.floor((d - today) / 86400000);
  } catch { return null; }
}

function dlcStatus(dlc) {
  const days = daysToDlc(dlc);
  if (days === null) return { kind: "none", label: "—", bg: "#F0F0EB", border: "#E5E5E0", text: "#A3A6A3" };
  if (days < 0) return { kind: "expired", label: `Périmée (${Math.abs(days)} j)`, bg: "#FBE5E0", border: "#E8C7BD", text: "#A04E2C", emoji: "🔴" };
  if (days === 0) return { kind: "today", label: "Aujourd'hui", bg: "#FBE5E0", border: "#E8C7BD", text: "#A04E2C", emoji: "🔴" };
  if (days <= 3) return { kind: "soon", label: `J-${days}`, bg: "#FBEEDB", border: "#E8D3A8", text: "#8C6B2F", emoji: "🟡" };
  if (days <= 7) return { kind: "warn", label: `J-${days}`, bg: "#FBF4E9", border: "#E8D3A8", text: "#8C6B2F", emoji: "🟡" };
  return { kind: "ok", label: `J-${days}`, bg: "#E8F1EA", border: "#B9D6BD", text: "#1F4E3D", emoji: "🟢" };
}

export default function DistributorSlots() {
  const { id } = useParams();
  const [distributor, setDistributor] = useState(null);
  const [distributors, setDistributors] = useState([]);
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingVisit, setOpeningVisit] = useState(false);

  const openVisit = async () => {
    setOpeningVisit(true);
    try {
      const { data } = await api.post(`/visits`, { distributor_id: id });
      window.location.href = `/visites/${data.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur ouverture visite");
    } finally {
      setOpeningVisit(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [d, l, p] = await Promise.all([
        api.get(`/distributors`),
        // Phase 98ap — Filtre côté backend pour éviter de rater des lots quand
        // le volume total dépasse la limite de 5000 lots retournés par /lots.
        api.get(`/lots?distributor_id=${id}`),
        api.get("/products"),
      ]);
      setDistributors(d.data || []);
      setDistributor((d.data || []).find((x) => x.id === id) || null);
      setLots((l.data || []).filter((x) => (x.quantity || 0) > 0));
      setProducts(p.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  // Regroupe les lots par slot
  const lotsBySlot = useMemo(() => {
    const out = {};
    for (const l of lots) {
      const slot = l.emplacement_number;
      if (slot == null) continue;
      if (!out[slot]) out[slot] = [];
      out[slot].push(l);
    }
    // Tri par DLC croissante (les plus urgents d'abord)
    for (const s of Object.keys(out)) {
      out[s].sort((a, b) => (a.dlc || "zzzz") < (b.dlc || "zzzz") ? -1 : 1);
    }
    return out;
  }, [lots]);

  const orphanLots = useMemo(() => lots.filter((l) => l.emplacement_number == null), [lots]);

  const slots = useMemo(() => distributor?.emplacements || [], [distributor]);

  const stats = useMemo(() => {
    let totalQty = 0, expired = 0, soon = 0, slotsUsed = 0, slotsTotal = slots.length;
    for (const s of slots) {
      const sLots = lotsBySlot[s] || [];
      if (sLots.length > 0) slotsUsed++;
      for (const l of sLots) {
        const status = dlcStatus(l.dlc);
        totalQty += l.quantity || 0;
        if (status.kind === "expired" || status.kind === "today") expired += l.quantity || 0;
        else if (status.kind === "soon") soon += l.quantity || 0;
      }
    }
    return { totalQty, expired, soon, slotsUsed, slotsTotal };
  }, [slots, lotsBySlot]);

  // Phase 63 — removeLot est remplacé par <WithdrawLotDialog> qui couvre
  // les 5 motifs (DLC, casse, blocage, retour Conthey, transfert). Le bouton
  // s'affiche désormais sur TOUS les lots (pas seulement les périmés/imminents)
  // et offre le choix du motif au lieu de forcer un OUT.

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#8C6B2F]" /></div>;
  }

  if (!distributor) {
    return (
      <div className="space-y-4">
        <Link to="/distributeurs" className="text-sm text-[#1F4E3D] hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Retour</Link>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-8 text-center text-[#A3A6A3] italic">Distributeur introuvable.</div>
      </div>
    );
  }

  const isDepot = isStockDepot(distributor) || distributor.type === "CLIENT";

  return (
    <div className="space-y-6" data-testid="distributor-slots-page">
      {/* Header */}
      <div>
        <Link to="/distributeurs" className="text-sm text-[#1F4E3D] hover:underline flex items-center gap-1 mb-3">
          <ArrowLeft className="w-4 h-4" /> Tous les distributeurs
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Building2 className="w-7 h-7 text-[#1F4E3D] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-heading">{distributor.name}</h2>
              <p className="text-sm text-[#A3A6A3]">
                {distributor.code && <span className="font-mono">{distributor.code}</span>}
                {distributor.city && <> · {distributor.city}</>}
                {distributor.type === "CLIENT" && <> · <span className="text-[#8C6B2F]">Client</span></>}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <InitialStockDialog
              distributorId={id}
              distributorName={distributor.name}
              onSaved={load}
            />
            {/* Phase 101gb — Bouton Resync stock Nayax : ne s'affiche que pour AUTOMATIQUE avec machine liée */}
            {distributor.type === "AUTOMATIQUE" && distributor.nayax_machine_id && (
              <NayaxResyncDialog
                distributorId={id}
                distributorName={distributor.name}
                onDone={load}
              />
            )}
            {!isDepot && <ContractShortcut distributorId={id} />}
            {!isDepot && (
              <EmptyMachineDialog
                distributorId={id}
                distributorName={distributor.name}
                lotCount={lots.length}
                totalQty={lots.reduce((s, l) => s + (l.quantity || 0), 0)}
                onDone={load}
              />
            )}
            <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-slots">
              <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
            </Button>
            <Link to={`/distributeurs/${id}/activite`}>
              <Button variant="outline" data-testid="open-activity">
                <BarChart3 className="w-4 h-4 mr-2" /> Activité
              </Button>
            </Link>
            {/* Phase 87bd — Bouton Mode Comptoir : UI dédiée tablette pour saisie rapide */}
            <Link to={`/distributeurs/${id}/comptoir`}>
              <Button className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="open-comptoir">
                <Building2 className="w-4 h-4 mr-2" /> Mode Comptoir
              </Button>
            </Link>
            {/* Phase 101fk (Feb 2026) — Module tablette visite unifiée */}
            {distributor.type === "AUTOMATIQUE" && (
              <Button
                onClick={openVisit}
                disabled={openingVisit}
                className="bg-[#7A1F1F] hover:bg-[#5F1717] text-white"
                data-testid="open-visit-tablet"
                title="Nouvelle visite : retrait DLC + comptage + chargement + PUT Nayax (module unifié)"
              >
                {openingVisit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
                Visite tablette
              </Button>
            )}
            {/* Phase 101gc — Mode terrain : outil tablette DLC-out + reappro-in pour livreurs */}
            {distributor.type === "AUTOMATIQUE" && (
              <Link to={`/tournee/${id}`}>
                <Button className="bg-[#8C6B2F] hover:bg-[#6F531F] text-white" data-testid="open-field-tour">
                  <Truck className="w-4 h-4 mr-2" /> Mode terrain
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">Slots utilisés</div>
          <div className="text-xl font-heading text-[#3D2914] mt-1">{stats.slotsUsed}<span className="text-xs text-[#A3A6A3] ml-1">/ {stats.slotsTotal}</span></div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">Barquettes totales</div>
          <div className="text-xl font-heading text-[#1F4E3D] mt-1">{stats.totalQty}</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">🟡 DLC à J-3 ou moins</div>
          <div className={`text-xl font-heading mt-1 ${stats.soon > 0 ? "text-[#8C6B2F]" : "text-[#A3A6A3]"}`}>{stats.soon}</div>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">🔴 Périmées (à retirer)</div>
          <div className={`text-xl font-heading mt-1 ${stats.expired > 0 ? "text-[#A04E2C]" : "text-[#A3A6A3]"}`}>{stats.expired}</div>
        </div>
      </div>

      {/* Phase 99a — Widget Réassort MOMA-calc (visible seulement pour distributeurs AUTOMATIQUE avec Nayax) */}
      {distributor.type === "AUTOMATIQUE" && distributor.nayax_machine_id && (
        <MomaReassortWidget distributorId={id} />
      )}

      {/* Phase 101 — Widget stock machine Nayax (source de vérité : GET machineProducts) */}
      {distributor.type === "AUTOMATIQUE" && (
        <div className="mb-4">
          <NayaxMachineWidget
            distributorId={id}
            nayaxMachineId={distributor.nayax_machine_id}
            distributorName={distributor.name}
          />
        </div>
      )}

      {/* Phase 101bi (14/02/2026) — Grille pavés lots AM retirée : source de vérité = snapshot MOMA
          affiché ci-dessus. Les DLC détaillées restent accessibles via :
          - Cartes stats en haut (agrégats J-3 + périmées)
          - Bouton « Activité » (historique complet)
          - Bouton « Mode Comptoir » (vue tablette)
          - Page /lots avec filtre distributeur */}

      {/* Lots sans slot (orphelins) */}
      {orphanLots.length > 0 && (
        <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded-lg p-4" data-testid="orphan-lots-section">
          <h3 className="text-sm font-semibold text-[#8C6B2F] mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Lots dans le dépôt mais sans slot assigné ({orphanLots.length})
          </h3>
          <p className="text-xs text-[#575A57] mb-3">Ces lots sont rattachés à ce dépôt sans emplacement précis. Affectez-leur un slot depuis Stocks & Lots.</p>
          <div className="space-y-1">
            {orphanLots.map((l) => {
              const product = productMap[l.product_id];
              const status = dlcStatus(l.dlc);
              return (
                <div key={l.id} className="flex items-center gap-2 bg-white rounded p-2 text-xs">
                  <span className="font-medium flex-1 truncate">{product?.name || l.product_id}</span>
                  <span className="font-mono text-[#A3A6A3]">{l.lot_number}</span>
                  <Badge variant="outline" style={{ backgroundColor: status.bg, borderColor: status.border, color: status.text }} className="text-[10px]">
                    DLC {l.dlc || "—"} · {status.label}
                  </Badge>
                  <span className="font-bold">×{l.quantity}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
